import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { getFishingRules } from "../../../../api";
import "../../../../styles/components/RulesTab.css";

export default function RulesTab({ data }) {
  const { t, i18n } = useTranslation();
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [generalContent, setGeneralContent] = useState(null);
  const [generalContentLoading, setGeneralContentLoading] = useState(false);
  const [generalContentError, setGeneralContentError] = useState(false);
  const isBasin = !!data?.isBasin;
  const prefetchedRules = isBasin ? null : data?.rules ?? null;
  const prefetchedLoading = isBasin ? false : !!data?.rulesLoading;
  const prefetchedError = isBasin ? false : !!data?.rulesError;
  const resolvedRules = prefetchedRules ?? rules;
  const contentRef = !isBasin ? resolvedRules?.contentRef ?? null : null;
  const preferredLanguage = useMemo(
    () => (i18n.language?.split("-")[0] || "en").toLowerCase(),
    [i18n.language]
  );
  const hasGeneralBucketCandidate =
    resolvedRules?.resolvedBucket === "country_freshwater" ||
    resolvedRules?.resolvedBucket === "country_marine";
  const missingGeneralBucket =
    !isBasin && hasGeneralBucketCandidate && !contentRef;
  const generalContentFallbackMessage =
    preferredLanguage === "bg" ? "Очаквайте скоро" : "Coming soon";
  const markdownPath = useMemo(() => {
    const markdownPaths = contentRef?.markdownPaths;

    if (!markdownPaths || typeof markdownPaths !== "object") {
      return null;
    }

    return (
      markdownPaths[preferredLanguage] ||
      markdownPaths.en ||
      Object.values(markdownPaths).find(Boolean) ||
      null
    );
  }, [contentRef, preferredLanguage]);
  // Fetch fishing rules
  useEffect(() => {
  // 🟦 BASIN MODE: do not call backend
  if (isBasin) {
    setRules(null);
    setLoading(false);
    setError(false);
    return;
  }

  if (prefetchedRules) {
    setRules(null);
    setLoading(false);
    setError(false);
    return;
  }

  if (prefetchedLoading) {
    setLoading(false);
    setError(false);
    return;
  }

  if (prefetchedError) {
    setLoading(false);
    setError(true);
    return;
  }

  let cancelled = false;

  async function fetchRules() {
    if (!data?.lat || !data?.lng) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    setRules(null);

    try {
      const result = await getFishingRules(data.lat, data.lng);

      if (cancelled) return;

      if (result) {
        setRules(result);
        setError(false);
      } else {
        setError(true);
      }
    } catch (err) {
      if (!cancelled) {
        console.error(err);
        setError(true);
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  }

  fetchRules();

  return () => {
    cancelled = true;
  };
}, [
  data?.lat,
  data?.lng,
  isBasin,
  prefetchedRules,
  prefetchedLoading,
  prefetchedError,
]);

// 🟦 BASIN MODE RENDER (local MD file)
useEffect(() => {
  if (isBasin || !contentRef?.jsonPath) {
    setGeneralContent(null);
    setGeneralContentLoading(false);
    setGeneralContentError(false);
    return;
  }

  let cancelled = false;

  async function fetchGeneralContent() {
    setGeneralContent(null);
    setGeneralContentLoading(true);
    setGeneralContentError(false);

    try {
      const jsonRequest = fetch(contentRef.jsonPath).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed loading rules JSON: ${response.status}`);
        }
        return response.json();
      });

      const markdownRequest = markdownPath
        ? fetch(markdownPath)
            .then((response) => {
              if (!response.ok) {
                throw new Error(
                  `Failed loading rules markdown: ${response.status}`
                );
              }
              return response.text();
            })
            .catch((err) => {
              console.error(err);
              return "";
            })
        : Promise.resolve("");

      const [contentJson, contentMarkdown] = await Promise.all([
        jsonRequest,
        markdownRequest,
      ]);

      if (cancelled) {
        return;
      }

      setGeneralContent({
        json: contentJson,
        markdown: contentMarkdown,
      });
    } catch (err) {
      if (!cancelled) {
        console.error(err);
        setGeneralContent(null);
        setGeneralContentError(true);
      }
    } finally {
      if (!cancelled) {
        setGeneralContentLoading(false);
      }
    }
  }

  fetchGeneralContent();

  return () => {
    cancelled = true;
  };
}, [contentRef?.jsonPath, isBasin, markdownPath]);

if (isBasin) {
  if (!data?.basinRulesMarkdown) {
    return (
      <div className="tab-content">
        <div className="info-panel-error">
          {t("rules_error")}
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content basin-rules">
      <div
        className="markdown-content"
        dangerouslySetInnerHTML={{
          __html: data.basinRulesMarkdown
            .replace(/^### (.*)$/gm, "<h3>$1</h3>")
            .replace(/^## (.*)$/gm, "<h2>$1</h2>")
            .replace(/^# (.*)$/gm, "<h1>$1</h1>")
            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
            // 🔗 Markdown links → real hyperlinks
            .replace(
              /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
              '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#1976d2;">$1</a>'
            )
            // keep line breaks
            .replace(/\n/g, "<br/>")
        }}
      />
    </div>
  );
}




  // Loading state
  if (prefetchedLoading || loading) {
    return (
      <div className="tab-content">
        <div className="info-panel-loading">
          {t("rules_loading")}
        </div>
      </div>
    );
  }

  // Error state (real failures)
  if (prefetchedError || error) {
    return (
      <div className="tab-content">
        <div className="info-panel-error">
          {t("rules_error")}
        </div>
      </div>
    );
  }

  // Still waiting for a non-aborted result
  if (!resolvedRules) {
    return (
      <div className="tab-content">
        <div className="info-panel-loading">
          {t("rules_loading")}
        </div>
      </div>
    );
  }

  const {
    fishingAllowed,
    forbiddenReason,
    countryName,
    locationName,
    waterType,
    license,
    localPermit,
    rules: ruleCodes = [],
    source = []
  } = resolvedRules;

  const hasLocalOverride = source.includes("local_water_override");

  return (
    <div className="tab-content">
      <h3>{t("weather_rules_title")}</h3>

      {/* 🛑 FORBIDDEN WARNING */}
      {!fishingAllowed && (
        <div className="rules-alert forbidden">
          <h4>{t("rules_forbidden_title")}</h4>
          <p>
            {forbiddenReason
              ? t(`rules_forbidden_reason.${forbiddenReason}`)
              : t("rules_forbidden_generic")}
          </p>
        </div>
      )}

      {/* 📍 CONTEXT */}
      <div className="rules-section">
        <p className="rules-item">
          <strong>{t("country")}:</strong> {countryName}
        </p>
        <p className="rules-item">
          <strong>{t("rules_water_type")}:</strong>{" "}
          {waterType === "saltwater"
            ? t("rules_saltwater")
            : t("rules_freshwater")}
        </p>

        {locationName && locationName !== "Unknown Location" && (
          <p className="rules-item">
            <strong>{t("rules_location")}:</strong> {locationName}
          </p>
        )}
      </div>

      <hr />

      {/* 🧾 LICENSE SECTION */}
      {fishingAllowed && license && (
        <div className="rules-section">
          <p className="rules-item">
            <strong>{t("weather_rules_license")}:</strong>{" "}
            <span className={license.required ? "text-warning" : "text-success"}>
              {license.required
                ? t("rules_license_required")
                : t("rules_license_not_required")}
            </span>
          </p>

          {license.authority && (
            <p className="rules-item">
              <strong>{t("rules_authority")}:</strong> {license.authority}
            </p>
          )}

          {license.url && (
            <button
              onClick={() => window.open(license.url, "_blank")}
              className="buy-license-button"
            >
              {t("weather_rules_buy")} / {t("weather_rules_visit_site")}
            </button>
          )}
        </div>
      )}

      {/* 🎫 LOCAL PERMIT (CLUB / PRIVATE WATER) */}
      {fishingAllowed && localPermit?.required && (
        <div className="local-rules-section">
          <h4>{t("rules_local_permit_required")}</h4>

          {localPermit.authority && (
            <p className="rules-item">
              <strong>{t("rules_authority")}:</strong> {localPermit.authority}
            </p>
          )}

          {localPermit.url && (
            <button
              onClick={() => window.open(localPermit.url, "_blank")}
              className="visit-site-button"
            >
              {t("weather_rules_visit_site")}
            </button>
          )}
        </div>
      )}

      {/* 📜 RULES LIST */}
      {ruleCodes.length > 0 && (
        <div className="rules-section">
          <p className="rules-item">
            <strong>{t("weather_rules_notes")}:</strong>
          </p>
          <ul className="rules-list">
            {ruleCodes.map((code, index) => (
              <li key={index}>{t(`rules.${code}`)}</li>
            ))}
          </ul>
        </div>
      )}

      {(contentRef || missingGeneralBucket) && (
        <div className="rules-section">
          {generalContentLoading && (
            <div className="info-panel-loading">
              {t("rules_loading")}
            </div>
          )}

          {!generalContentLoading && generalContentError && (
            <div className="info-panel-error">
              {t("rules_error")}
            </div>
          )}

          {!generalContentLoading &&
            !generalContentError &&
            generalContent?.json &&
            generalContent?.markdown && (
              <div className="markdown-content">
                <ReactMarkdown
                  components={{
                    a: ({ children, ...props }) => (
                      <a
                        {...props}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {generalContent.markdown}
                </ReactMarkdown>
              </div>
            )}

          {!generalContentLoading &&
            !generalContentError &&
            missingGeneralBucket && (
              <div className="general-content-fallback">
                {generalContentFallbackMessage}
              </div>
            )}
        </div>
      )}

      <hr />

      {/* 💡 TIPS */}
      <div className="rules-section">
        <h3>{t("weather_tips_title")}</h3>
        <ul className="tips-list">
          <li>{t("weather_tips_barbless")}</li>
          <li>{t("weather_tips_limits")}</li>
          <li>{t("weather_tips_litter")}</li>
          <li>{t("weather_tips_safety")}</li>
        </ul>
      </div>

      {/* ℹ️ SOURCE */}
      <div className="rules-footer">
        <small>
          {t("rules_source")}:{" "}
          {hasLocalOverride
            ? t("rules_source_local")
            : t("rules_source_country")}
        </small>
      </div>
    </div>
  );
}
